import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';

export type CameraStatus = 'idle' | 'requesting' | 'live' | 'error';

interface UseCameraOptions {
  facingMode?: 'environment' | 'user';
  /** Auto-start on mount. Default true. */
  autoStart?: boolean;
}

/**
 * Camera lifecycle hook. Handles iOS Safari quirks:
 *   - getUserMedia requires HTTPS or localhost.
 *   - On tab background, iOS pauses the track; we re-acquire on visibilitychange.
 *   - Returns the active MediaStream so a <video> element can sink it.
 */
export function useCamera(options: UseCameraOptions = {}) {
  const { facingMode = 'environment', autoStart = true } = options;
  const streamRef = useRef<MediaStream | null>(null);
  // Reentrancy guard for start(). Refs (not state) so the gate is correct
  // synchronously — React state updates wouldn't have landed yet when
  // ensureLive() calls stop()→start() back-to-back.
  const requestingRef = useRef(false);
  const [status, setStatus] = useState<CameraStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    requestingRef.current = false;
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    // Gate on refs, NOT on React state — see requestingRef declaration above.
    if (requestingRef.current || streamRef.current) return;
    requestingRef.current = true;
    setStatus('requesting');
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera API unavailable. Use Safari over HTTPS.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      setStatus('live');
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : 'Could not access camera.';
      setError(msg);
      setStatus('error');
    } finally {
      requestingRef.current = false;
    }
  }, [facingMode]);

  // Auto-start
  useEffect(() => {
    if (autoStart) void start();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Lifecycle — re-acquire the camera stream after any kind of
       interruption (background, screen lock, incoming call, FaceTime,
       low-power throttling, or iOS audio-session pre-emption).

       Listens on two independent channels:
         (A) Capacitor's `App.appStateChange` — most reliable inside
             WKWebView; fires for native lifecycle transitions even
             when `document.visibilitychange` is delayed by iOS.
         (B) DOM `visibilitychange` — keeps the web PWA build working
             without modification, and acts as a redundant safety
             net inside the native shell.
         (C) The `MediaStreamTrack.onended` event itself — catches the
             case where iOS unilaterally kills the track (memory
             warning, hardware pre-empted by Phone app) without an
             accompanying app-state change. Immediate signal. ─── */
  const ensureLive = useCallback(() => {
    const tracks = streamRef.current?.getVideoTracks() ?? [];
    if (tracks.length === 0) return;
    if (tracks.some((t) => t.readyState !== 'live')) {
      console.log('[camera] Track not live — restarting');
      stop();
      void start();
    }
  }, [start, stop]);

  // (A) + (B) — app-resume / tab-visible
  useEffect(() => {
    let detachAppListener: (() => Promise<void>) | undefined;

    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) ensureLive();
    }).then((h) => {
      detachAppListener = () => h.remove();
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') ensureLive();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      void detachAppListener?.();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ensureLive]);

  // (C) — track-level events. Re-bound whenever the stream changes.
  useEffect(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const tracks = stream.getVideoTracks();

    const onEnded = () => {
      console.log('[camera] MediaStreamTrack ended — restarting');
      stop();
      void start();
    };

    tracks.forEach((t) => {
      t.addEventListener('ended', onEnded);
    });

    return () => {
      tracks.forEach((t) => {
        t.removeEventListener('ended', onEnded);
      });
    };
    // `status` is included so this effect re-runs when start() swaps in
    // a fresh stream; the ref alone wouldn't trigger React.
  }, [status, start, stop]);

  return {
    stream: streamRef.current,
    status,
    error,
    start,
    stop,
  };
}
